/**
 * Releases — Phase 3.
 *
 * Lists every row in `ota_releases`, grouped by channel, with three
 * mutating actions (per `docs/PHASE_3_AGENT_PROMPT.md` Task 6):
 *
 *   • Upload — multipart POST /api/admin/releases. The signature input is
 *     the operator's own `release-3.0.0/sign.py` output; the backend
 *     rejects with 422 if it doesn't verify against `OTA_PUBLIC_KEY`.
 *   • Promote / demote — PATCH /api/admin/releases/:version with `{channel}`.
 *   • Delete — DELETE /api/admin/releases/:version. Returns 409 + the
 *     pinned device list if any device is currently pinned to this version.
 *
 * Style + structure match `Devices.tsx` (orange + teal tokens, surface card
 * containers). All mutations use `react-query` + `invalidateQueries` so the
 * list refetches automatically after each action.
 */

import { useMemo, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import PageHeader from "@/components/PageHeader";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import {
  ApiError,
  API_BASE,
  deleteRelease,
  listReleases,
  patchRelease,
  uploadRelease,
} from "@/lib/api";
import { formatBytes, formatDateTime, shortSha } from "@/lib/format";
import type {
  OtaRelease,
  ReleasePatch,
  ReleaseUploadInputs,
} from "@/lib/types";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Releases() {
  const [channelFilter, setChannelFilter] = useState<string>("");
  const queryClient = useQueryClient();

  const releasesQ = useQuery({
    queryKey: ["releases", channelFilter],
    queryFn: () => listReleases(channelFilter || undefined),
  });

  const grouped = useMemo(() => {
    const out = new Map<string, OtaRelease[]>();
    for (const r of releasesQ.data ?? []) {
      const list = out.get(r.channel) ?? [];
      list.push(r);
      out.set(r.channel, list);
    }
    // Sort each channel's releases newest-first.
    for (const list of out.values()) {
      list.sort((a, b) => b.released_at.localeCompare(a.released_at));
    }
    return out;
  }, [releasesQ.data]);

  // Build the channel dropdown from whatever we've actually seen plus the
  // two well-known names. Keeps the UI responsive even before the first
  // fetch completes.
  const knownChannels = useMemo(() => {
    const set = new Set<string>(["stable", "beta"]);
    for (const r of releasesQ.data ?? []) set.add(r.channel);
    return Array.from(set).sort();
  }, [releasesQ.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["releases"] });
  };

  const uploadMutation = useMutation({
    mutationFn: uploadRelease,
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader
        title="Releases"
        subtitle={
          releasesQ.data
            ? `${releasesQ.data.length} release${releasesQ.data.length === 1 ? "" : "s"} ${channelFilter ? `on ${channelFilter}` : "across all channels"}`
            : "Loading releases…"
        }
      />

      <div className="px-8 py-6 space-y-6">
        <UploadReleaseCard
          mutation={uploadMutation}
          knownChannels={knownChannels}
        />

        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-brand-dim">
              Filter by channel
            </label>
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            >
              <option value="">All channels</option>
              {knownChannels.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          {channelFilter ? (
            <button
              type="button"
              onClick={() => setChannelFilter("")}
              className="text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
            >
              Clear
            </button>
          ) : null}
        </div>

        {releasesQ.isLoading ? (
          <LoadingBlock label="Loading releases…" />
        ) : releasesQ.isError ? (
          <ErrorBlock error={releasesQ.error} onRetry={() => releasesQ.refetch()} />
        ) : !releasesQ.data || releasesQ.data.length === 0 ? (
          <EmptyBlock
            title="No releases yet."
            description="Upload your first signed firmware tarball using the form above."
          />
        ) : (
          <div className="space-y-6">
            {Array.from(grouped.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([channel, releases]) => (
                <ReleasesGroup
                  key={channel}
                  channel={channel}
                  releases={releases}
                  knownChannels={knownChannels}
                  onMutated={invalidate}
                />
              ))}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Upload form
// ---------------------------------------------------------------------------

function UploadReleaseCard({
  mutation,
  knownChannels,
}: {
  mutation: UseMutationResult<OtaRelease, Error, ReleaseUploadInputs>;
  knownChannels: string[];
}) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState("");
  const [channel, setChannel] = useState("beta");
  const [signature, setSignature] = useState("");
  const [notes, setNotes] = useState("");
  const [minFromVersion, setMinFromVersion] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setFile(null);
    setVersion("");
    setChannel("beta");
    setSignature("");
    setNotes("");
    setMinFromVersion("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    mutation.mutate(
      {
        file,
        version: version.trim(),
        channel: channel.trim(),
        signature: signature.trim(),
        notes: notes.trim() || undefined,
        min_from_version: minFromVersion.trim() || undefined,
      },
      {
        onSuccess: () => {
          reset();
        },
      },
    );
  };

  return (
    <Card title="Upload new release" subtitle="Phase 3 — offline signing">
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-xs text-brand-dim leading-relaxed">
          Sign the tarball locally with{" "}
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            release-3.0.0/sign.py
          </code>
          , then drop it here together with the version, target channel and
          the base64 signature it printed. The backend re-verifies against{" "}
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            OTA_PUBLIC_KEY
          </code>{" "}
          before storing — a mismatch returns 422 and nothing is written.
        </p>

        <DropZone
          file={file}
          setFile={setFile}
          fileInputRef={fileInputRef}
          dragOver={dragOver}
          setDragOver={setDragOver}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Labeled label="Version" hint="e.g. 3.0.1 or 3.0.1-beta.1">
            <input
              required
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="3.0.1-beta.1"
              autoComplete="off"
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            />
          </Labeled>
          <Labeled
            label="Channel"
            hint="lowercase alnum + hyphens (stable, beta, customer-acme)"
          >
            <input
              required
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              list="known-channels"
              placeholder="beta"
              autoComplete="off"
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            />
            <datalist id="known-channels">
              {knownChannels.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Labeled>
        </div>

        <Labeled
          label="Signature (base64 ed25519)"
          hint="from sign.py — paste the single-line signature value"
        >
          <textarea
            required
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            rows={2}
            spellCheck={false}
            placeholder="MEUCIQDx…"
            className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-xs font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange resize-y"
          />
        </Labeled>

        <div className="grid gap-4 sm:grid-cols-2">
          <Labeled
            label="Notes (optional)"
            hint="markdown OK; surfaced in audit log + API"
          >
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange resize-y"
              placeholder="What changed in this release"
            />
          </Labeled>
          <Labeled
            label="Min from version (optional)"
            hint="reject install if device is below this baseline"
          >
            <input
              value={minFromVersion}
              onChange={(e) => setMinFromVersion(e.target.value)}
              autoComplete="off"
              placeholder="3.0.0"
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            />
          </Labeled>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={
              !file || !version || !channel || !signature || mutation.isPending
            }
            className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Uploading…" : "Upload release"}
          </button>
          {file ? (
            <button
              type="button"
              onClick={reset}
              className="text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
            >
              Reset
            </button>
          ) : null}
          {mutation.isSuccess && !mutation.isPending ? (
            <span className="inline-flex items-center gap-2 text-xs text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Published {mutation.data.version} → {mutation.data.channel}
            </span>
          ) : null}
        </div>

        {mutation.isError ? (
          <UploadErrorBlock error={mutation.error} />
        ) : null}
      </form>
    </Card>
  );
}

function DropZone({
  file,
  setFile,
  fileInputRef,
  dragOver,
  setDragOver,
}: {
  file: File | null;
  setFile: (f: File | null) => void;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  dragOver: boolean;
  setDragOver: (b: boolean) => void;
}) {
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) setFile(f);
      }}
      onClick={() => fileInputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
      }}
      className={[
        "rounded-md border-2 border-dashed px-4 py-6 text-center cursor-pointer select-none transition-colors",
        dragOver
          ? "border-brand-orange bg-orange-500/5"
          : "border-surface-border bg-surface-sunken hover:border-brand-orange/60",
      ].join(" ")}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
        className="hidden"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      {file ? (
        <div className="space-y-1">
          <div className="text-sm text-slate-100 font-mono break-all">
            {file.name}
          </div>
          <div className="text-xs text-brand-dim">
            {formatBytes(file.size)} · click or drop another to replace
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="text-sm text-slate-200">
            Drop firmware tarball here, or click to choose
          </div>
          <div className="text-xs text-brand-dim">
            .tar.gz produced by <code>ota_release.py build</code>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Tries to map well-known backend error codes onto operator-actionable
 * advice. Falls through to the default ErrorBlock for anything else.
 */
function UploadErrorBlock({ error }: { error: unknown }) {
  if (error instanceof ApiError) {
    if (error.reason === "signature_invalid") {
      return (
        <CalloutError title="Signature invalid">
          The signature didn't verify against the server's{" "}
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            OTA_PUBLIC_KEY
          </code>
          . Re-run <code>release-3.0.0/sign.py</code> against this exact
          tarball and paste the resulting signature, or confirm the server
          public key matches the operator key in{" "}
          <code>build/keys/ota_pubkey.pem</code>.
        </CalloutError>
      );
    }
    if (error.reason === "version_already_exists") {
      const detail = error.detail as
        | { version?: string; existing_channel?: string }
        | undefined;
      return (
        <CalloutError title="Version already exists">
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            {detail?.version ?? "this version"}
          </code>{" "}
          is already published
          {detail?.existing_channel
            ? ` on the ${detail.existing_channel} channel`
            : ""}
          . Bump the semver tag (e.g. add a{" "}
          <code className="font-mono">.1</code> suffix) and re-sign.
        </CalloutError>
      );
    }
    if (error.reason === "file_too_large") {
      const detail = error.detail as
        | { max_bytes?: number; got_bytes?: number }
        | undefined;
      return (
        <CalloutError title="Tarball too large">
          {formatBytes(detail?.got_bytes ?? null)} exceeds the{" "}
          {formatBytes(detail?.max_bytes ?? null)} cap. Either trim the
          payload or raise the cap in <code>admin_releases.ts</code>.
        </CalloutError>
      );
    }
  }
  return <ErrorBlock error={error} />;
}

function CalloutError({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-4 py-3">
      <div className="text-sm font-medium text-rose-200">{title}</div>
      <div className="mt-1 text-xs text-rose-100/90 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Releases group (per channel)
// ---------------------------------------------------------------------------

function ReleasesGroup({
  channel,
  releases,
  knownChannels,
  onMutated,
}: {
  channel: string;
  releases: OtaRelease[];
  knownChannels: string[];
  onMutated: () => void;
}) {
  const newest = releases[0];
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ChannelChip channel={channel} />
          <span>{channel}</span>
        </span>
      }
      subtitle={
        newest
          ? `${releases.length} release${releases.length === 1 ? "" : "s"} · newest ${newest.version}`
          : "no releases"
      }
    >
      <div className="overflow-x-auto -mx-4 -my-2">
        <table className="w-full text-sm">
          <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
            <tr>
              <th className="px-4 py-2 font-medium">Version</th>
              <th className="px-4 py-2 font-medium">SHA256</th>
              <th className="px-4 py-2 font-medium">Size</th>
              <th className="px-4 py-2 font-medium">Released</th>
              <th className="px-4 py-2 font-medium">By</th>
              <th className="px-4 py-2 font-medium">Notes</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {releases.map((r) => (
              <ReleaseRow
                key={r.version}
                release={r}
                knownChannels={knownChannels}
                onMutated={onMutated}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ReleaseRow({
  release,
  knownChannels,
  onMutated,
}: {
  release: OtaRelease;
  knownChannels: string[];
  onMutated: () => void;
}) {
  const [showPromote, setShowPromote] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [copied, setCopied] = useState(false);

  const onCopyManifest = async () => {
    // The manifest URL is what a device would ask for. We point at the
    // backend (not the R2 object) since the backend is the authority on
    // pinning + presigning.
    const url = `${API_BASE || ""}/api/v1/manifest?channel=${encodeURIComponent(release.channel)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard rejected (insecure context or user denied) — fall back
      // to a window prompt so the operator still gets the value.
      window.prompt("Copy manifest URL:", url);
    }
  };

  return (
    <>
      <tr className="hover:bg-surface-raised/60">
        <td className="px-4 py-1.5 font-mono text-xs text-slate-100">
          {release.version}
        </td>
        <td
          className="px-4 py-1.5 font-mono text-xs text-slate-300"
          title={release.tarball_sha256}
        >
          {shortSha(release.tarball_sha256)}
        </td>
        <td className="px-4 py-1.5 text-slate-200 whitespace-nowrap">
          {formatBytes(release.size_bytes)}
        </td>
        <td
          className="px-4 py-1.5 text-slate-200 whitespace-nowrap"
          title={release.released_at}
        >
          {formatDateTime(release.released_at)}
        </td>
        <td className="px-4 py-1.5 text-slate-300">
          {release.released_by ?? "—"}
        </td>
        <td className="px-4 py-1.5 text-slate-300 max-w-xs truncate">
          {release.notes ?? <span className="text-brand-dim">—</span>}
        </td>
        <td className="px-4 py-1.5 text-right whitespace-nowrap">
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={onCopyManifest}
              className="text-xs text-brand-dim hover:text-slate-200"
              title="Copy GET /api/v1/manifest URL for this channel"
            >
              {copied ? "Copied!" : "Copy URL"}
            </button>
            <button
              type="button"
              onClick={() => setShowPromote(true)}
              className="rounded border border-surface-border bg-surface-sunken px-2 py-0.5 text-xs text-slate-200 hover:border-brand-orange/60"
            >
              Move
            </button>
            <button
              type="button"
              onClick={() => setShowDelete(true)}
              className="rounded border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-xs text-rose-200 hover:bg-rose-500/20"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {showPromote ? (
        <PromoteReleaseDialog
          release={release}
          knownChannels={knownChannels}
          onClose={() => setShowPromote(false)}
          onSuccess={onMutated}
        />
      ) : null}
      {showDelete ? (
        <DeleteReleaseDialog
          release={release}
          onClose={() => setShowDelete(false)}
          onSuccess={onMutated}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Dialogs (rendered in portal-ish overlay; no library dependency)
// ---------------------------------------------------------------------------

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <tr>
      <td colSpan={7} className="p-0">
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-lg rounded-lg border border-surface-border bg-surface shadow-xl">
            <header className="flex items-center justify-between border-b border-surface-border px-5 py-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-100">
                {title}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-brand-dim hover:text-slate-100 text-lg leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="px-5 py-4">{children}</div>
          </div>
        </div>
      </td>
    </tr>
  );
}

function PromoteReleaseDialog({
  release,
  knownChannels,
  onClose,
  onSuccess,
}: {
  release: OtaRelease;
  knownChannels: string[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [target, setTarget] = useState(release.channel);
  const [notes, setNotes] = useState(release.notes ?? "");
  const mutation = useMutation({
    mutationFn: (patch: ReleasePatch) => patchRelease(release.version, patch),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  const dirty = target !== release.channel || (notes ?? "") !== (release.notes ?? "");

  return (
    <ModalShell
      title={`Move ${release.version}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const patch: ReleasePatch = {};
          if (target !== release.channel) patch.channel = target.trim();
          if ((notes ?? "") !== (release.notes ?? "")) {
            patch.notes = notes.trim() === "" ? null : notes;
          }
          mutation.mutate(patch);
        }}
        className="space-y-4"
      >
        <p className="text-xs text-brand-dim leading-relaxed">
          Currently on{" "}
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            {release.channel}
          </code>
          . Devices on the destination channel will pull this version on
          their next manifest poll. Edits are recorded in the audit log.
        </p>
        <Labeled label="Channel">
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            list="known-channels-promote"
            autoComplete="off"
            className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
          />
          <datalist id="known-channels-promote">
            {knownChannels.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Labeled>
        <Labeled label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange resize-y"
          />
        </Labeled>
        <div className="flex items-center gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!dirty || mutation.isPending}
            className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </div>
        {mutation.isError ? <ErrorBlock error={mutation.error} /> : null}
      </form>
    </ModalShell>
  );
}

function DeleteReleaseDialog({
  release,
  onClose,
  onSuccess,
}: {
  release: OtaRelease;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => deleteRelease(release.version),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  return (
    <ModalShell title={`Delete ${release.version}?`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-200">
          This removes both the R2 object and the{" "}
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            ota_releases
          </code>{" "}
          row. Devices currently on this version will keep running it
          (the manifest just won't return it again on their next poll).
        </p>
        <p className="text-xs text-brand-dim">
          If any device is{" "}
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            pinned_version
          </code>{" "}
          to this release the request fails with 409 and lists the pinning
          devices below.
        </p>

        {mutation.isError ? <DeleteErrorBlock error={mutation.error} /> : null}

        <div className="flex items-center gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="inline-flex items-center rounded bg-rose-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Deleting…" : "Delete release"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function DeleteErrorBlock({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.reason === "delete_blocked_by_pin") {
    const detail = error.detail as
      | {
          version?: string;
          pinned_count?: number;
          pinned_devices?: Array<{ serial: string; device_label: string | null }>;
        }
      | undefined;
    return (
      <CalloutError title="Cannot delete — devices are pinned">
        <div>
          {detail?.pinned_count ?? "Some"} device(s) currently have{" "}
          <code className="rounded bg-surface-sunken px-1.5 py-0.5">
            pinned_version = {detail?.version ?? release_label_fallback(error)}
          </code>
          . Clear or repoint the pin on each device first.
        </div>
        {detail?.pinned_devices?.length ? (
          <ul className="mt-2 space-y-1 text-xs">
            {detail.pinned_devices.map((d) => (
              <li key={d.serial} className="font-mono">
                <a
                  href={`#/devices/${encodeURIComponent(d.serial)}`}
                  className="text-rose-100 hover:underline"
                >
                  {d.serial}
                </a>
                {d.device_label ? (
                  <span className="ml-2 font-sans not-italic text-rose-100/80">
                    ({d.device_label})
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </CalloutError>
    );
  }
  return <ErrorBlock error={error} />;
}

function release_label_fallback(_err: ApiError): string {
  return "unknown";
}

// ---------------------------------------------------------------------------
// Tiny presentational primitives
// ---------------------------------------------------------------------------

function Card({
  title,
  subtitle,
  children,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-surface-border bg-surface">
      <header className="flex items-center justify-between border-b border-surface-border px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-100">
          {title}
        </h2>
        {subtitle ? (
          <span className="text-xs text-brand-dim">{subtitle}</span>
        ) : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function Labeled({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] uppercase tracking-wider text-brand-dim">
        {label}
      </label>
      {children}
      {hint ? <span className="text-[11px] text-brand-dim">{hint}</span> : null}
    </div>
  );
}

function ChannelChip({ channel }: { channel: string }) {
  // stable = orange (production), beta = teal (preview), other = neutral.
  const tone =
    channel === "stable"
      ? "bg-brand-orange/15 text-orange-200 ring-1 ring-inset ring-brand-orange/40"
      : channel === "beta"
        ? "bg-brand-teal/15 text-cyan-200 ring-1 ring-inset ring-brand-teal/40"
        : "bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-mono ${tone}`}
    >
      {channel}
    </span>
  );
}
